/* eslint-disable react/sort-comp, no-console */
import { node, object, bool } from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as Sentry from '@sentry/browser';
import cx from 'classnames';
import { Box, Stack, Button, ButtonGroup, Text } from '@chaser/components';
import { withRouter } from 'react-router';
import FetchError from '../../util/FetchError';
import ApiError from '../../util/ApiError';
import style from './ErrorBoundary.module.scss';
import SentryFeedbackModal from '../SentryFeedbackModal';
import RouterButton from '../RouterButton';
import { getErrorMessage } from '../../util/errors';
import ChaserLogoErrorPage from '../../assets/Chaser-Logo-Error-Page.svg';
import BillingError from '../../util/BillingError';
import TotpRequiredError from '../../util/TotpRequiredError';
import SwapOrganisationsMenu from './SwapOrganisation';

class ErrorBoundary extends Component {
  static propTypes = {
    children: node.isRequired,
    history: object.isRequired,
    center: bool,
    organisation: object,
  };

  static defaultProps = {
    center: false,
  };

  state = {
    error: null,
    eventId: null,
    feedbackModalOpen: false,
  };

  static getDerivedStateFromError(error) {
    if (error instanceof Error) {
      return { error };
    }

    return null;
  }

  componentDidCatch(err, info) {
    if (err.log === false) return;

    Sentry.withScope(scope => {
      if (err.code) scope.setTag('code', err.code);
      if (err.info) scope.setExtras(err.info);

      scope.setLevel('fatal');
      scope.setExtras(info);

      const error = new Error(err);
      error.stack = err.stack;

      const eventId = Sentry.captureException(error);
      this.setState({
        eventId,
      });
    });
  }

  componentDidMount() {
    this.retry();
    this.unlisten = this.props.history.listen(() => {
      if (this.state.error) {
        this.setState({ error: null });
      }
    });
    window.addEventListener('online', this.retry);
  }

  componentDidUpdate(prevProps) {
    if (
      this.state.error &&
      prevProps.organisation.ShortCode !== this.props.organisation.ShortCode
    ) {
      this.setState({ error: null, eventId: null, feedbackModalOpen: false });
    }
  }

  componentWillUnmount() {
    this.unlisten();
    window.removeEventListener('online', this.retry);
  }

  retry = () => {
    const { error } = this.state;
    if (error) {
      if (error.retry) {
        error.retry();
      }
      this.setState({ error: null, eventId: null, feedbackModalOpen: false });
    }
  };

  renderError() {
    const { error, eventId, feedbackModalOpen } = this.state;
    const showFeedbackOption =
      error && eventId && navigator.onLine !== false && !error.message.includes('Loading chunk'); // TODO: show to all users again when it works
    const isBillingError = error instanceof BillingError;
    const isTotpError = error instanceof TotpRequiredError;

    const message = getErrorMessage(error);
    let button = <Button onClick={() => window.location.reload()}>Reload</Button>;

    switch (true) {
      case isTotpError:
        button = <RouterButton to="/user/security">Continue to setup</RouterButton>;
        break;
      case isBillingError:
        button = (
          <Button onClick={() => (window.location.href = 'mailto:support@chaser.io')}>
            Contact support
          </Button>
        );
        break;
      case error instanceof FetchError:
        button = <Button onClick={this.retry}>Try again</Button>;
        break;
      case error instanceof ApiError:
        button = error.action ? (
          <Button onClick={error.action.callback}>{error.action.label}</Button>
        ) : (
          <RouterButton to="/">Return to Chaser</RouterButton>
        );
        break;
      default:
    }

    return (
      <>
        <img alt="ChaserLogoErrorPage" src={ChaserLogoErrorPage} />
        <Text fontSize="medium" fontWeight="bold">
          {message}
        </Text>
        {showFeedbackOption && !isBillingError && !isTotpError && (
          <Text fontSize="medium" color="Mid Neutral 1">
            There was an unexpected problem, let us know what happened.
          </Text>
        )}
        {isTotpError && (
          <Text fontSize="medium" color="Mid Neutral 1">
            2FA is a type of multi-factor authentication that provides an added layer of security to
            your account when logging in.
          </Text>
        )}
        {isBillingError && (
          <Text fontSize="medium" color="Mid Neutral 1">
            Please contact our support
          </Text>
        )}
        <SwapOrganisationsMenu
          organisation={this.props.organisation}
          organisations={this.props.user.organisations}
        />
        <ButtonGroup>
          {showFeedbackOption && !isBillingError && !isTotpError && (
            <Button
              appearance="secondary"
              onClick={() => this.setState({ feedbackModalOpen: true })}
            >
              Tell us about it
            </Button>
          )}
          {button}
        </ButtonGroup>
        {showFeedbackOption && !isBillingError && (
          <SentryFeedbackModal
            open={feedbackModalOpen}
            toggle={() => this.setState({ feedbackModalOpen: false })}
            eventId={eventId}
          />
        )}
      </>
    );
  }

  render() {
    const { error } = this.state;
    const { children, center } = this.props;

    return error ? (
      <Box p="medium" className={cx(center && style.center)}>
        <Stack
          gap="medium"
          style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}
        >
          {this.renderError()}
        </Stack>
      </Box>
    ) : (
      children
    );
  }
}

export default connect(({ organisationReducer, loginReducer }) => ({
  organisation: organisationReducer,
  user: loginReducer,
}))(withRouter(ErrorBoundary));
